import { useEffect, useState, useCallback } from "react"
import { supabase, logAuditAction } from "@/lib/supabase"
import { useToast } from "@/context/ToastContext"
import { Modal, FormGroup, Input, Select, Textarea } from "@/components/common/Modal"
import { Button } from "@/components/common/Button"
import { Badge } from "@/components/common/Badge"
import { EmptyState } from "@/components/common/EmptyState"
import { LoadingRow } from "@/components/common/Spinner"
import { Plus, RefreshCw } from "lucide-react"
import { useAuth } from "@/context/AuthContext"

interface Adjustment {
  id: string
  created_at: string
  member_id: string
  type: string
  amount: number
  notes: string | null
  users?: { email: string; full_name: string | null } | null
}

interface UserOption { id: string; email: string; full_name: string | null }

export function AdjustmentsPage() {
  const { toast } = useToast()
  const { user: adminUser } = useAuth()
  const [data, setData]       = useState<Adjustment[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen]       = useState(false)
  const [saving, setSaving]   = useState(false)

  const [search, setSearch]     = useState("")
  const [results, setResults]   = useState<UserOption[]>([])
  const [selUser, setSelUser]   = useState<UserOption | null>(null)
  const [adjType, setAdjType]   = useState("credit")
  const [amount, setAmount]     = useState("")
  const [reason, setReason]     = useState("")

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data: rows, error } = await supabase
        .from("commission_transactions")
        .select("*, users(email, full_name)")
        .in("type", ["credit", "debit", "bonus", "correction", "refund_clawback"])
        .order("created_at", { ascending: false })
        .limit(100)
      if (error) throw error
      setData((rows ?? []) as Adjustment[])
    } catch { toast("Failed to load adjustments", "error") }
    finally { setLoading(false) }
  }, [toast])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (search.length < 2) { setResults([]); return }
    supabase.from("users").select("id, email, full_name")
      .or(`email.ilike.%${search}%,full_name.ilike.%${search}%`).limit(5)
      .then(({ data }) => setResults((data ?? []) as UserOption[]))
  }, [search])

  const handleSave = async () => {
    if (!selUser || !amount || !reason) {
      toast("User, amount and reason required", "error"); return
    }
    const amt = parseFloat(amount)
    if (isNaN(amt) || amt <= 0) { toast("Valid amount required", "error"); return }
    setSaving(true)
    try {
      const { error } = await supabase.from("commission_transactions").insert({
        member_id:  selUser.id,
        type:       adjType,
        amount:     ["debit", "refund_clawback"].includes(adjType) ? -amt : amt,
        status:     "approved",
        notes:      reason,
        created_at: new Date().toISOString(),
      })
      if (error) throw error
      await logAuditAction(
        "commission_adjustment",
        selUser.id,
        `${adjType} $${amt.toFixed(2)} — ${reason}`,
        selUser.email
      )
      toast("Adjustment applied", "success")
      setOpen(false)
      setSelUser(null); setAmount(""); setReason(""); setSearch("")
      load()
    } catch (err: unknown) {
      toast(err instanceof Error ? err.message : "Failed", "error")
    } finally { setSaving(false) }
  }

  const typeColor = (t: string) => {
    if (["credit", "bonus"].includes(t)) return "success"
    if (["debit", "refund_clawback"].includes(t)) return "danger"
    return "info" as const
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between">
        <div />
        <div className="flex gap-2">
          <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
          <Button variant="primary" icon={<Plus size={14} />} onClick={() => setOpen(true)}>
            New Adjustment
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {["Date", "User", "Type", "Amount", "Reason"].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <LoadingRow cols={5} /> :
               data.length === 0 ? (
                <tr><td colSpan={5}>
                  <EmptyState icon="✏️" title="No adjustments yet" subtitle="Manual commission adjustments will appear here." />
                </td></tr>
               ) : data.map(a => (
                <tr key={a.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                    {new Date(a.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-semibold text-sm text-gray-900">{a.users?.full_name ?? "—"}</p>
                    <p className="text-xs text-gray-400">{a.users?.email}</p>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={typeColor(a.type)}>{a.type?.replace(/_/g, " ")}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`font-black tabular-nums text-sm ${a.amount >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {a.amount >= 0 ? "+" : ""}${Math.abs(a.amount).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                    {a.notes ?? "—"}
                  </td>
                </tr>
               ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New Adjustment" icon="✏️"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleSave}>Apply Adjustment</Button>
          </>
        }
      >
        <FormGroup label="User" required>
          {selUser ? (
            <div className="flex items-center justify-between p-3 rounded-xl bg-zimrah-50 border border-zimrah-200">
              <div>
                <p className="font-bold text-sm">{selUser.full_name ?? "—"}</p>
                <p className="text-xs text-gray-500">{selUser.email}</p>
              </div>
              <button onClick={() => { setSelUser(null); setSearch("") }}
                className="text-xs text-red-500 hover:text-red-700 font-semibold">Remove</button>
            </div>
          ) : (
            <div>
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by email or name…" />
              {results.length > 0 && (
                <div className="mt-1 border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  {results.map(u => (
                    <button key={u.id} onClick={() => { setSelUser(u); setSearch(""); setResults([]) }}
                      className="w-full text-left px-4 py-2.5 hover:bg-zimrah-50 transition-colors border-b border-gray-50 last:border-0">
                      <p className="font-semibold text-sm text-gray-900">{u.full_name ?? "—"}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </FormGroup>

        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Type">
            <Select value={adjType} onChange={e => setAdjType(e.target.value)}>
              <option value="credit">Credit (+)</option>
              <option value="debit">Debit (-)</option>
              <option value="bonus">Bonus (+)</option>
              <option value="correction">Correction</option>
              <option value="refund_clawback">Refund Clawback (-)</option>
            </Select>
          </FormGroup>
          <FormGroup label="Amount ($)" required>
            <Input type="number" step="0.01" min="0.01" value={amount}
              onChange={e => setAmount(e.target.value)} placeholder="0.00" />
          </FormGroup>
        </div>

        <FormGroup label="Reason" required>
          <Textarea value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Explain the reason for this adjustment…" />
        </FormGroup>
      </Modal>
    </div>
  )
}